import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Highlighter from 'react-highlight-words';
import useServerPagination from '../../hooks/useServerPagination';
import PaginationControls from './PaginationControls';
import SearchFilter from './SearchFilter';
import NodeInstance from '../../Global/nodeAxios';

const SmartDownloadPagination = ({
  // API Configuration
  apiEndpoint,
  downloadEndpoint,
  token,
  
  // Table Configuration
  columns = [],
  
  // Layout Configuration - only table supported now
  enableHighlighting = true,
  
  // Pagination Configuration
  defaultPageSize = 50,
  pageSizeOptions = [50, 100, 500, 1000],
  
  // Download Configuration
  downloadFilename = 'Report',
  enableDownload = true,
  
  // Search Configuration
  enableSearch = true,
  searchPlaceholder = "Search across all columns...",
  searchDebounceMs = 300,
  onSearchClick = null, // ADD: handle explicit search button click
  
  // Trigger for cache invalidation
  trigger = null,

  skipInitialLoad = false, // ADD: option to skip initial load
  
  // UI Configuration
  uiConfig = {
    container: 'pt-2 uppercase',
    searchInput: 'input input-sm h-10 pl-10 pr-4 w-80 input-bordered focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase',
    downloadButton: 'p-2 px-4 rounded-md bg-blue disabled:bg-gray-500 text-white min-w-fit text-sm flex items-center gap-2',
    controlsLayout: 'flex-wrap gap-2 w-full items-center flex flex-row-reverse',
    tableContainer: 'overflow-x-auto bg-white rounded-lg shadow max-h-[70vh]',
    table: 'min-w-full',
    tableHeader: 'bg-gray-50 sticky top-0 z-20 shadow-sm',
    tableHeaderCell: 'px-6 py-4 text-left text-base font-semibold text-gray-700 uppercase tracking-wider bg-gray-50',
    tableBody: 'bg-white',
    tableCell: 'px-6 py-6 whitespace-nowrap text-base text-gray-700',
    paginationContainer: 'flex flex-col items-center w-full',
    paginationControls: 'flex md:gap-10 sm:gap-4 justify-center items-center bg-white p-1 text-center rounded-md mb-1 w-full',
    paginationButton: 'p-2 rounded-md transition-colors duration-200',
    paginationButtonActive: 'hover:bg-blue/90 hover:text-white cursor-pointer bg-white',
    paginationButtonDisabled: 'cursor-not-allowed opacity-50 bg-gray-200 text-gray-400',
    pageSelect: 'cursor-pointer p-1 rounded',
    pageSizeSelect: 'cursor-pointer p-1 rounded',
    recordsInfo: 'text-sm text-gray-600'
  },
  
  // Custom Actions
  customActions = [],
  
  // Additional Action Buttons (rendered separately from download)
  additionalActions = [],

  // Left-side nav buttons (Links, custom JSX rendered before search/download)
  headerLeft = null,
  
  // Custom Legend Component
  legendComponent = null,
  
  // Customization
  renderCustomCell = null,
  onRowClick = null,
  getRowClassName = null,
  
  // Event Handlers
  onDataLoad = null,
  onSearchChange = null, // ADD: monitor search term changes
  onDownloadStart = null,
  onDownloadComplete = null,
  onDownloadError = null,
  
  // Data Override
  manualData = null, // ADD: override server data with manual data (e.g. search results)
  
  // Styling
  className = '',
  tableClassName = '',
  headerClassName = '',
  
  // Additional Props
  noDataMessage = "No data found"
}) => {
  // Simple state management
  const [searchTerm, setSearchTerm] = useState('');
  const [downloadOption, setDownloadOption] = useState('all');
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Refs for cleanup
  const componentMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    componentMountedRef.current = true;
    return () => {
      componentMountedRef.current = false;
    };
  }, []);

  // Server-side pagination with search
  const {
    data: serverData,
    paginationState: serverPaginationState,
    goToPage,
    changePageSize,
    refresh
  } = useServerPagination({
    apiEndpoint,
    token,
    defaultPageSize,
    searchTerm,
    searchDebounceMs,
    trigger,
    skipInitialLoad
  });

  const data = manualData || serverData;
  const paginationState = manualData ? {
    ...serverPaginationState,
    totalRecords: manualData.length,
    totalPages: 1,
    currentPage: 1,
    isLoading: false
  } : serverPaginationState;

  // Build search parameters for download
  const downloadSearchParams = useMemo(() => {
    const params = {};
    if (searchTerm.trim()) {
      params.search = searchTerm.trim();
    }
    return params;
  }, [searchTerm]);

  // Simple download handler
  const handleDownload = useCallback(async () => {
    if (!downloadEndpoint || isDownloading) return;

    setIsDownloading(true);

    try {
      let downloadParams = { ...downloadSearchParams };
      let filename = downloadFilename;

      if (downloadOption === 'current') {
        downloadParams = {
          ...downloadSearchParams,
          page: paginationState.currentPage,
          limit: paginationState.pageSize
        };
        filename = `${downloadFilename}_page_${paginationState.currentPage}`;
      }

      if (onDownloadStart) {
        onDownloadStart({ option: downloadOption, params: downloadParams });
      }

      const params = new URLSearchParams(downloadParams);
      const downloadUrl = `${downloadEndpoint}${params.toString() ? `?${params}` : ''}`;

      const response = await NodeInstance.get(downloadUrl, {
        responseType: 'blob',
        headers: { 
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        },
      });

      if (!componentMountedRef.current) return;

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      const finalFilename = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
      link.setAttribute("download", finalFilename);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
      
      if (onDownloadComplete) {
        onDownloadComplete({ 
          success: true, 
          recordCount: downloadOption === 'current' ? data.length : paginationState.totalRecords,
          filename: finalFilename,
          option: downloadOption
        });
      }

    } catch (error) {
      console.error('Download error:', error);
      if (onDownloadError && componentMountedRef.current) {
        onDownloadError({ 
          success: false, 
          error: error.response?.data?.message || error.message || "Download failed",
          option: downloadOption
        });
      }
    } finally {
      if (componentMountedRef.current) {
        setIsDownloading(false);
      }
    }
  }, [
    downloadEndpoint, isDownloading, downloadSearchParams, downloadFilename, downloadOption,
    paginationState.currentPage, paginationState.pageSize, paginationState.totalRecords,
    data.length, token, onDownloadStart, onDownloadComplete, onDownloadError
  ]);

  // Search words for highlighting
  const currentSearchWords = useMemo(() => {
    return searchTerm ? searchTerm.split(/\s+/).filter(Boolean) : [];
  }, [searchTerm]);

  // Data load callback
  const handleDataLoad = useCallback(() => {
    if (onDataLoad) {
      onDataLoad(data, paginationState);
    }
  }, [onDataLoad, data, paginationState]);

  useEffect(() => {
    handleDataLoad();
  }, [handleDataLoad]);

  // Default custom cell renderer
  const defaultRenderCustomCell = useCallback((item, column, rowIndex, currentPage, pageSize) => {
    if (['serial', 'sno', 'index', 'SNO.'].includes(column.key)) {
      return (currentPage - 1) * pageSize + rowIndex + 1;
    }
    
    if (column.key === 'visa_provider') {
      return item.visa_provider || item.Overseas_visa_provider || "-";
    }
    
    return null;
  }, []);

  // Render cell content with highlighting
  const renderCellContent = useCallback((value, searchWords) => {
    if (!enableHighlighting || !searchWords.length) {
      return value || '-';
    }
    
    return (
      <Highlighter
        searchWords={searchWords}
        autoEscape={true}
        textToHighlight={String(value || '-')}
      />
    );
  }, [enableHighlighting]);

  // Search change handler
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  }, [onSearchChange]);

  // Download option change handler
  const handleDownloadOptionChange = useCallback((e) => {
    setDownloadOption(e.target.value);
  }, []);

  // Download options
  const downloadOptions = useMemo(() => [
    { value: 'all', label: `All Results (${paginationState.totalRecords})` },
    { value: 'current', label: data.length > 0 ? `Current Page (${data.length})` : 'Current Page (0)' }
  ], [paginationState.totalRecords, data.length]);

  // Simple static download button
  const StaticDownloadButton = useMemo(() => {
    // Download should be available if there are any total records, not just current filtered results
    const hasDataToDownload = paginationState.totalRecords > 0;
    const isButtonDisabled = !downloadEndpoint || isDownloading || !hasDataToDownload || paginationState.isLoading;
    
    const buttonStyle = {
      backgroundColor: isButtonDisabled ? '#9ca3af' : undefined,
      cursor: isButtonDisabled ? 'not-allowed' : 'pointer'
    };

    return (
      <button
        className={`${uiConfig.downloadButton} ${isDownloading ? 'animate-pulse' : ''}`}
        style={buttonStyle}
        onClick={handleDownload}
        disabled={isButtonDisabled}
      >
        <div className="flex items-center gap-2">
          {isDownloading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          <span>{isDownloading ? 'Downloading...' : 'Download'}</span>
        </div>
      </button>
    );
  }, [isDownloading, downloadEndpoint, uiConfig.downloadButton, handleDownload, paginationState.totalRecords, paginationState.isLoading]);

  // Table layout
  const renderTableLayout = useMemo(() => (
    <div className={uiConfig.tableContainer}>
      <table className={`${uiConfig.table} ${tableClassName}`}>
        <thead className={uiConfig.tableHeader}>
          <tr className="bg-gray-50">
            {columns.map((column, index) => (
              <th
                key={column.key || `header-${index}`}
                className={uiConfig.tableHeaderCell}
                style={{ backgroundColor: '#f9fafb' }}
              >
                {column.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={uiConfig.tableBody}>
          {paginationState.isLoading ? (
            <tr>
              <td colSpan={columns.length} className="px-6 py-8 text-center">
                <div className="flex items-center justify-center">
                  <span className="loading loading-spinner loading-md mr-2"></span>
                  Loading data...
                </div>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-6 py-8 text-center text-gray-500">
                {searchTerm ? `No results found for "${searchTerm}"` : noDataMessage}
              </td>
            </tr>
          ) : (
            data.map((item, rowIndex) => {
              const rowExtraClass = getRowClassName ? getRowClassName(item) : '';
              const isHighlighted = rowExtraClass.includes('bg-red');
              return (
              <tr 
                key={item.id || item.candidate_id || `row-${rowIndex}`}
                className={`border-b-[1.5px] border-slate-100 ${isHighlighted ? '' : 'hover:bg-gray-50'} ${rowExtraClass}`}
                style={isHighlighted ? { backgroundColor: '#fecaca' } : {}}
                onClick={() => onRowClick?.(item)}
              >
                {columns.map((column, colIndex) => (
                  <td key={column.key || `cell-${colIndex}`} className={uiConfig.tableCell}>
                    {(() => {
                      // Try custom cell renderer first
                      if (renderCustomCell) {
                        const customResult = renderCustomCell(item, column, rowIndex, paginationState.currentPage, paginationState.pageSize);
                        if (customResult !== null && customResult !== undefined) return customResult;
                      }
                      
                      // Try default custom cell renderer
                      const defaultResult = defaultRenderCustomCell(item, column, rowIndex, paginationState.currentPage, paginationState.pageSize);
                      if (defaultResult !== null && defaultResult !== undefined) return defaultResult;
                      
                      // Try column render function
                      if (column.render) {
                        return column.render(item[column.key], item, rowIndex);
                      }
                      
                      // Default cell content with highlighting
                      return renderCellContent(item[column.key], currentSearchWords);
                    })()}
                  </td>
                ))}
              </tr>
            );})
          )}
        </tbody>
      </table>
    </div>
  ), [
    uiConfig, tableClassName, columns, paginationState.isLoading, paginationState.currentPage, 
    paginationState.pageSize, data, searchTerm, noDataMessage, onRowClick, renderCustomCell, 
    defaultRenderCustomCell, renderCellContent, currentSearchWords, getRowClassName
  ]);

  return (
    <div className={`${uiConfig.container} ${className}`}>
      {/* Custom Legend */}
      {legendComponent && (
        <div className="mb-4">
          {legendComponent}
        </div>
      )}
      
      {/* Toolbar with Filters, Search and Actions aligned to the right */}
      <div className={`flex flex-wrap items-center justify-end gap-2 mb-4 ${headerClassName}`}>
        {/* Render headerLeft (Filters/Dropdowns) */}
        {headerLeft && (
          <div className="flex flex-wrap items-center gap-2">
            {headerLeft}
          </div>
        )}

        {/* Search and Actions Container */}
        <div className="flex flex-wrap items-center gap-2">
          {enableSearch && (
            <div className="flex gap-2 items-center">
              <SearchFilter
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                placeholder={searchPlaceholder}
                inputClassName={uiConfig.searchInput}
                disabled={false}
                isLoading={paginationState.isLoading && searchTerm.length > 0}
              />
              {onSearchClick && (
                <button
                  className="px-4 rounded-md py-2 text-sm h-[36px] text-white bg-blue cursor-pointer whitespace-nowrap"
                  onClick={() => onSearchClick(searchTerm)}
                  disabled={paginationState.isLoading || !searchTerm}
                >
                  Search
                </button>
              )}
            </div>
          )}
          
          <div className="flex gap-2 items-center">
            {enableDownload && downloadEndpoint && (
              <div className="flex gap-2 items-center">
                <select
                  className={`select select-sm select-bordered focus:outline-none ${
                    isDownloading || paginationState.totalRecords === 0 || paginationState.isLoading
                      ? 'cursor-not-allowed opacity-50 bg-gray-100' 
                      : 'cursor-pointer'
                  }`}
                  value={downloadOption}
                  onChange={handleDownloadOptionChange}
                  disabled={isDownloading || paginationState.totalRecords === 0 || paginationState.isLoading}
                >
                  {downloadOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                
                {StaticDownloadButton}
              </div>
            )}
            
            {/* Additional Action Buttons */}
            {additionalActions.map((action, index) => (
              <button
                key={`additional-${index}`}
                onClick={action.onClick}
                className={`${action.className} whitespace-nowrap`}
                style={action.style}
                disabled={action.disabled}
                title={action.tooltip}
              >
                {action.icon && <span className="mr-2">{action.icon}</span>}
                {action.label}
              </button>
            ))}
            
            {customActions.map((action, index) => (
              <button
                key={index}
                onClick={action.onClick}
                className={`${action.className || uiConfig.downloadButton} whitespace-nowrap`}
                disabled={action.disabled}
              >
                {action.icon && <span className="mr-2">{action.icon}</span>}
                {action.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Data Display */}
      <div>
        {/* Mobile slide indicator */}
        <p className="text-sm w-full text-right font-medium mb-1 xl:hidden block">
          {`Slide / scroll to right >`}
        </p>
        
        {renderTableLayout}
      </div>

      {/* Pagination Controls */}
      <PaginationControls
        currentPage={paginationState.currentPage}
        totalPages={paginationState.totalPages}
        pageSize={paginationState.pageSize}
        totalRecords={paginationState.totalRecords}
        pageSizeOptions={pageSizeOptions}
        onPageChange={goToPage}
        onPageSizeChange={changePageSize}
        isLoading={paginationState.isLoading}
        uiConfig={{
          container: uiConfig.paginationContainer,
          paginationContainer: uiConfig.paginationControls,
          paginationButton: uiConfig.paginationButton,
          paginationButtonActive: uiConfig.paginationButtonActive,
          paginationButtonDisabled: uiConfig.paginationButtonDisabled,
          pageSelect: uiConfig.pageSelect,
          pageSizeSelect: uiConfig.pageSizeSelect,
          recordsInfo: uiConfig.recordsInfo
        }}
      />

      {/* Error Display */}
      {paginationState.error && (
        <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          <p>Error: {paginationState.error}</p>
          <button
            onClick={refresh}
            className="mt-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
};

// PropTypes for type safety
SmartDownloadPagination.propTypes = {
  // API Configuration
  apiEndpoint: PropTypes.string.isRequired,
  downloadEndpoint: PropTypes.string,
  token: PropTypes.string.isRequired,
  
  // Table Configuration
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    searchable: PropTypes.bool,
    render: PropTypes.func
  })).isRequired,
  
  // Layout Configuration
  enableHighlighting: PropTypes.bool,
  
  // Pagination Configuration
  defaultPageSize: PropTypes.number,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  
  // Download Configuration
  downloadFilename: PropTypes.string,
  enableDownload: PropTypes.bool,
  
  // Search Configuration
  enableSearch: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  searchDebounceMs: PropTypes.number,
  
  // Trigger for cache invalidation
  trigger: PropTypes.any,
  
  // UI Configuration
  uiConfig: PropTypes.object,
  
  // Custom Actions
  customActions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    icon: PropTypes.node
  })),
  
  // Additional Action Buttons
  additionalActions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    icon: PropTypes.node,
    tooltip: PropTypes.string
  })),
  
  // Custom Legend Component
  legendComponent: PropTypes.node,
  
  // Customization
  renderCustomCell: PropTypes.func,
  onRowClick: PropTypes.func,
  
  // Event Handlers
  onDataLoad: PropTypes.func,
  onDownloadStart: PropTypes.func,
  onDownloadComplete: PropTypes.func,
  onDownloadError: PropTypes.func,
  
  // Styling
  className: PropTypes.string,
  tableClassName: PropTypes.string,
  headerClassName: PropTypes.string,
  
  // Additional Props
  noDataMessage: PropTypes.string
};

export default SmartDownloadPagination;